/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { TourItemType } from '@bb/domain/dist/types';
import { SortableHandle } from 'react-sortable-hoc';

import {
  Icon,
  ListItem,
  ListItemMeta,
  Tag,
  Text,
  ListingImage,
  theme,
} from 'components';
import { useBreakpoint } from 'hooks';
import { CANCELLED_STATUSES } from 'components/List/ListItems';
import TourShowingTimeSelector from './TourShowingTimeSelector';
import TourNoteInput from './TourNoteInput';
import { TourItemContent, TourModalType } from '../types';
import TourListingContext from '../TourListingContext';
import ListItemAction from './ListItemAction';
import StopThumbnail from './StopThumbnail';
import TourAcceptDenyShowing from './TourAcceptDenyShowing';
import { StyledDivider } from '../StyledComponents';
import TourShowingStatus from './TourShowingStatus';
import TravelTime from './TravelTime';
import MobileFriendlyTooltip from '../MobileFriendlyTooltip';
import { renderTourItemApprovalTypeLabel } from '../utils';

export interface RouteListingOrStopProps {
  item: TourItemContent;
  isLastItem?: boolean;
  isSorting?: boolean;
  isBeingSorted?: boolean;
}

const RouteListingOrStop: React.FC<RouteListingOrStopProps> = ({
  item,
  isLastItem,
  isSorting,
  isBeingSorted,
}) => {
  const {
    loading,
    setModalType,
    setListingId,
    setSelectedTourItem,
    setListingModalState,
  } = useContext(TourListingContext);
  const [textHovering, setTextHovering] = useState(false);
  const [prevTextHovering, setPrevTextHovering] = useState(false);
  const { md } = useBreakpoint();

  useEffect(() => {
    setPrevTextHovering(textHovering);
  }, [textHovering]);

  const [dragIconHovering, setDragIconHovering] = useState(false);
  // only show address description for stops that also have a business/location name (ie. Starbucks, The Rom)
  const showAddress =
    item.type === TourItemType.ROUTE_STOP && item.locationName !== item.address;

  const isItemCancelled = useMemo(
    () => CANCELLED_STATUSES.includes(item.status),
    [item],
  );
  // Instead of using :hover css class, we are listening for mouse hover event,
  // since for stops, both the title and the description of the item should be underlined,
  // even if only one is hovered
  const onTextHover = () => {
    setTextHovering(true);
  };

  const onTextHoverOut = () => {
    setTextHovering(false);
  };

  const onIconMouseOver = () => {
    setDragIconHovering(true);
  };

  const onIconMouseOut = () => {
    setDragIconHovering(false);
  };

  const onTourItemClick = () => {
    if (
      (item.type === TourItemType.APPOINTMENT || item.appointmentId) &&
      item.listingId
    ) {
      if (loading) return;
      setModalType(TourModalType.LISTING);
      // Need them separately since listings that are not yet added will not have
      // TourItemContent, but only listingId -> handled from Universal Search
      setListingId(item.listingId);
      setSelectedTourItem(item);
      setListingModalState(true);
    } else if (item.type === TourItemType.ROUTE_STOP) {
      const queryText = `${item.locationName}, ${item.address}`;
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${queryText}`,
      );
    }
  };

  return (
    <StyledListItem $isBeingSorted={isBeingSorted} $md={md}>
      <ListItemMeta
        avatar={
          <StyledAvatarContainer>
            {!isItemCancelled && (
              <StyledIconContainer
                onMouseLeave={onIconMouseOut}
                onMouseEnter={onIconMouseOver}
                $isBeingSorted={isBeingSorted}
              >
                <MobileFriendlyTooltip
                  open={dragIconHovering && !isSorting}
                  title={'Drag to reorder'}
                >
                  <DragHandle />
                </MobileFriendlyTooltip>
              </StyledIconContainer>
            )}
            <MobileFriendlyTooltip
              title="View listing"
              open={
                (prevTextHovering || textHovering) &&
                item.type !== TourItemType.ROUTE_STOP
              }
            >
              {item.appointmentId || item.type === TourItemType.APPOINTMENT ? (
                <StyledThumbnailWrapper
                  onMouseOver={onTextHover}
                  onMouseOut={onTextHoverOut}
                  onClick={onTourItemClick}
                >
                  <ListingImage imageSrc={item.thumbnail} size={36} />
                </StyledThumbnailWrapper>
              ) : (
                <StopThumbnail
                  onClick={onTourItemClick}
                  isClickable
                  onMouseOver={onTextHover}
                  onMouseOut={onTextHoverOut}
                />
              )}
            </MobileFriendlyTooltip>
          </StyledAvatarContainer>
        }
        title={
          <StyledTitleContainer>
            <MobileFriendlyTooltip
              // @ts-ignore (percentage string is allowed yet not typed in source code)
              align={{ targetOffset: ['50%', '0%'] }}
              open={
                (prevTextHovering || textHovering) &&
                item.type === TourItemType.ROUTE_STOP
              }
              title={'View on Google Maps'}
            >
              <StyledTextContainer
                onClick={onTourItemClick}
                onMouseOver={onTextHover}
                onMouseOut={onTextHoverOut}
              >
                <StyledText ellipsis $hovered={textHovering}>
                  {item.locationName ?? item.address}
                </StyledText>
              </StyledTextContainer>
            </MobileFriendlyTooltip>
            {item.autoConfirm && (
              <AutoConfirmTag color={'green1'}>Auto-Confirmed</AutoConfirmTag>
            )}
            {item.isUnclaimed && (
              <MobileFriendlyTooltip
                title={
                  'This listing is not represented by BrokerBay. Please ensure to book via the correct showing service.'
                }
              >
                <StyledTag color="red">Not BrokerBay</StyledTag>
              </MobileFriendlyTooltip>
            )}

            <ListItemAction item={item} />
          </StyledTitleContainer>
        }
        description={
          <StyledDescription>
            {showAddress && (
              <StyledAddressContainer
                onClick={onTourItemClick}
                onMouseOver={onTextHover}
                onMouseOut={onTextHoverOut}
              >
                <StyledText ellipsis $hovered={textHovering}>
                  {item.address}
                </StyledText>
              </StyledAddressContainer>
            )}
            {(item.type === TourItemType.APPOINTMENT || item.appointmentId) && (
              <>
                {!item.isUnclaimed ? (
                  <>
                    <TourShowingTimeSelector item={item} />
                    <TourShowingStatus item={item} />
                  </>
                ) : (
                  <TourNoteInput item={item} />
                )}
                <TourAcceptDenyShowing item={item} />
              </>
            )}
            {!isItemCancelled &&
              item?.status !== 'confirm' &&
              item.type !== TourItemType.ROUTE_STOP && (
                <StyledText block color={theme.gray7}>
                  {renderTourItemApprovalTypeLabel(item)}
                </StyledText>
              )}
            {!isLastItem && <StyledDivider />}
            {!CANCELLED_STATUSES.includes(item.status) && (
              <TravelTime itemId={item.id} />
            )}
          </StyledDescription>
        }
      />
    </StyledListItem>
  );
};

export default RouteListingOrStop;

const DragHandle = SortableHandle(() => <StyledIcon type="antdMoreOutlined" />);

const StyledIconContainer = styled.div<{
  $isBeingSorted?: boolean;
}>`
  position: absolute;
  left: -21px;
  top: 20%;
  display: none;
  cursor: grab;
  ${({ $isBeingSorted }) => $isBeingSorted && `cursor: grabbing;`}
`;

const StyledIcon = styled(Icon)<{ $mouseDown?: boolean }>`
  font-size: 20px;
  stroke: grey;
  stroke-width: 8%;
`;

const StyledListItem = styled(ListItem)<{
  $isBeingSorted?: boolean;
  $md: boolean;
}>`
  padding: ${({ $md }) => ($md ? '12px 24px 0px 24px' : '12px 12px 0px 12px')};
  pointer-events: all;
  :hover ${StyledIconContainer} {
    display: ${({ $md }) => ($md ? 'block' : 'none')};
  }
  :active ${StyledIconContainer} {
    cursor: grabbing;
  }
  ${({ $md }) =>
    $md &&
    `
      transition: all 0.3s ease;
    `}
  ${({ $isBeingSorted }) =>
    $isBeingSorted &&
    `
      cursor: grabbing;
      transition-property: box-shadow, background-color;
      box-shadow: 3px 3px 3px 3px rgba(0, 0, 0, 0.15);
      background-color: white;
    `}
`;

const StyledAvatarContainer = styled.div`
  position: relative;
`;

const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StyledText = styled(Text)<{ $hovered?: boolean }>`
  white-space: nowrap;
  margin: 0;
  padding-right: 2rem;
  ${({ $hovered }) => $hovered && 'text-decoration: underline;'}
  && {
    width: 100%;
  }
`;

const StyledTag = styled(Tag)`
  border-radius: 2px;
  margin-right: 45px;
`;

const AutoConfirmTag = styled(Tag)`
  border: 1px solid ${theme.green6};
  color: ${theme.green6};
  border-radius: 2px;
  margin-left: 5px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  position: relative;
  flex-wrap: wrap;
  cursor: pointer;
  padding-right: 25px;
`;

const StyledAddressContainer = styled.span`
  cursor: pointer;

  // extend the padding to better capture mouse events.
  padding-top: 5px;
  position: relative;
  top: -5px;
`;

const StyledThumbnailWrapper = styled.span`
  width: 36px;
  height: 36px;
  cursor: pointer;
`;

const StyledTextContainer = styled.div`
  width: 100%;
  display: flex;
`;
